import type { ChatLayoutHeaderActionsProps } from './types';
import { ChatLayoutContext } from 'Components/Chat/contexts/ChatLayoutContext';
import { BodyCopy } from 'Components/shared/BodyCopy';
import { IconButton } from 'Components/shared/IconButton';
import { MobXProviderContext, observer } from 'mobx-react';
import { useIsPersonInactive } from 'Modules/conversation/hooks/useIsPersonInactive';
import { useCallRouter } from 'Modules/softphone/hooks';
import { useUIStore } from 'Modules/ui/index.store';
import * as React from 'react';

import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { placeVideoCallConference } from 'Utils/placeVideoCallConference';
import { Popover, PopoverContent, PopoverTrigger } from '~/lib/ui/popover';
import { ChatLayoutHeaderMoreOptions } from './ChatLayoutHeaderMoreOptions';
import { Styled } from './index.styles';

export const ChatLayoutHeaderActions: React.FC<ChatLayoutHeaderActionsProps> =
  observer(
    ({
      call,
      conversation,
      isGroupChat,
      isAdHocGroupChat,
      isOwnConversation = false,
      personDetails,
      testid: parentTestid,
      video,
    }) => {
      const {
        conversation: conversationStore,
        ui: { listOfPinnedMessages },
      } = React.useContext<RootStoreProps>(MobXProviderContext);

      // Use the unified router hook
      const { callWithPerson } = useCallRouter();

      const { entitySidePanelInfo, setEntitySidePanelInfo } = useUIStore();

      const { view, setView } = React.useContext(ChatLayoutContext);
      const testid = `${parentTestid}-chatLayoutHeaderActions`;

      const isPersonInactive = useIsPersonInactive(conversation);

      const [showDropdown, setShowDropdown] = React.useState(false);

      const placeCall = () => {
        // Route call through the router hook
        if (call.type === 'personId') {
          void callWithPerson(call.value, undefined);
        } else {
          void callWithPerson(undefined, call.value);
        }

        pushToGTMDataLayer('placeCall', {
          internalUser: true,
          callDestination: call.value,
          conversationId: conversation.id,
        });
      };

      const isPinned = view === 'pinned-messages';
      const pinnedMessagesCounter = listOfPinnedMessages
        ?.get(conversation.id)
        ?.filter(
          ({ systemEvent, call, conference }) =>
            !(systemEvent?.eventType || call?.text || conference?.displayName)
        )?.length;

      React.useEffect(() => {
        if (entitySidePanelInfo?.contact) {
          if (personDetails) {
            setEntitySidePanelInfo({
              entityId: personDetails.id.toString(),
              entityType: personDetails.type,
            });
          }
          if (isGroupChat) {
            setEntitySidePanelInfo({
              groupConversationId: conversation.id,
            });
            conversationStore.setChannelInfoDetails(conversation.id, 'channel');
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [entitySidePanelInfo?.contact?.id, personDetails, isGroupChat]);

      return (
        <Styled.Wrapper>
          {conversationStore?.CurrentConversation?.isActiveParticipant && (
            <>
              {!isPersonInactive && (
                <>
                  {video && (
                    <Styled.IconWrapper>
                      <IconButton
                        icon="video"
                        variant="secondary"
                        onClick={() =>
                          void placeVideoCallConference({
                            conversation: conversationStore,
                            conversationId: conversation.id,
                          })
                        }
                        disabled={{
                          title: 'Video conference unavailable',
                          condition: video?.disabled,
                        }}
                        size="medium"
                        testid={`${testid}-buttonVideo`}
                      />
                      <BodyCopy noMargin>Video</BodyCopy>
                    </Styled.IconWrapper>
                  )}
                  {call && (
                    <Styled.IconWrapper>
                      <IconButton
                        icon="call"
                        variant="secondary"
                        onClick={placeCall}
                        size="medium"
                        testid={`${testid}-buttonCall`}
                      />
                      <BodyCopy noMargin>Call</BodyCopy>
                    </Styled.IconWrapper>
                  )}
                </>
              )}

              {!!pinnedMessagesCounter && (
                <Styled.IconWrapper>
                  <IconButton
                    icon="pin"
                    variant="secondary"
                    active={isPinned}
                    onClick={() => setView(isPinned ? '' : 'pinned-messages')}
                    size="medium"
                    testid={`${testid}-buttonViewPinnedMessages`}
                    counter={pinnedMessagesCounter}
                  />
                  <BodyCopy noMargin>{isPinned ? 'Hide' : 'View'}</BodyCopy>
                </Styled.IconWrapper>
              )}
            </>
          )}

          {!isOwnConversation && (
            <Popover
              open={showDropdown}
              onOpenChange={setShowDropdown}
              modal={false}
            >
              <PopoverTrigger asChild>
                <Styled.IconWrapper>
                  <IconButton
                    icon="more"
                    variant="secondary"
                    onClick={() => setShowDropdown(!showDropdown)}
                    size="medium"
                    testid={`${testid}-buttonMore`}
                  />
                  <BodyCopy noMargin>More</BodyCopy>
                </Styled.IconWrapper>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="end"
                avoidCollisions={false}
                updatePositionStrategy="always"
                className="min-w-[190px] px-[3px] py-[10px]"
              >
                <ChatLayoutHeaderMoreOptions
                  {...{
                    conversationId: conversation.id,
                    isGroupChat,
                    isAdHocGroupChat,
                    personDetails,
                    setShowDropdown,
                    testid,
                  }}
                />
              </PopoverContent>
            </Popover>
          )}
        </Styled.Wrapper>
      );
    }
  );
